import React, { Component } from 'react';
import { compose } from 'recompose';
import { connect, FormikProps } from 'formik';
import { differenceBy } from 'lodash';
import { Paper } from '@material-ui/core';
import { createStyles, withStyles, Theme, WithStyles } from '@material-ui/core/styles';

import {
  withClient,
  withUser,
  IWithClient,
  IWithUser,
  withSocialConnections,
  IWithSocialConnections,
} from 'providers';
import { connectionPopup } from 'helpers';

import { INetwork, IShare } from '../types';
import { NETWORKS } from '../constants';
import { ActiveAuth, ActiveUnauth, Inactive } from '../components';
import { Feature, GetSharePreviewInfoQuery } from 'api';

interface IProps {
  onConnect: () => void;
  previewInfo: GetSharePreviewInfoQuery['getSharePreviewInfo'];
}

interface IPropsEnhanced
  extends IProps,
    IWithClient,
    IWithUser,
    IWithSocialConnections,
    WithStyles<typeof styles> {
  formik: FormikProps<IShare>;
}

const styles = (theme: Theme) =>
  createStyles({
    item: {
      padding: '12px 16px 8px',
      marginBottom: theme.spacing(3),
    },
  });

const enhancers = compose<IPropsEnhanced, IProps>(
  connect,
  withUser,
  withClient,
  withSocialConnections,
  withStyles(styles),
);

class Networks extends Component<IPropsEnhanced> {
  componentDidUpdate(prevProps: IPropsEnhanced) {
    const { connections, formik, onConnect } = this.props;
    const prevConnections = prevProps.connections;

    if (connections.length === prevConnections.length) {
      return;
    }

    const [addedNetwork] = differenceBy(connections, prevConnections, 'id');
    formik.setFieldValue(`${addedNetwork.provider}.network_id`, addedNetwork.id);
    formik.setFieldValue(`${addedNetwork.provider}.active`, true);
    onConnect();
  }

  handleConnect = ({ provider }: INetwork) => {
    const { user, client } = this.props;
    connectionPopup(user.id, provider, (client.features || []).includes(Feature.LegacyAuthDomain));
  };

  handleToggle = (network: INetwork, active: boolean) => {
    const { formik } = this.props;
    const { provider } = network;

    formik.setFieldValue(`${provider}.active`, active);
  };

  renderInactiveNetwork(network: INetwork) {
    const { classes } = this.props;

    return (
      <Paper key={network.provider} className={classes.item}>
        <Inactive network={network} onConnect={() => this.handleConnect(network)} />
      </Paper>
    );
  }

  renderActiveNetwork(network: INetwork) {
    const { classes, connections, previewInfo } = this.props;

    const accounts = connections.filter(account => account.provider === network.provider);
    const isAuthed = accounts.length && network.auth;

    if (isAuthed) {
      return (
        <Paper key={network.provider} className={classes.item}>
          <ActiveAuth
            network={network}
            accounts={accounts}
            onConnect={this.handleConnect}
            previewInfo={previewInfo}
          />
        </Paper>
      );
    }

    return (
      <Paper key={network.provider} className={classes.item}>
        <ActiveUnauth network={network} />
      </Paper>
    );
  }

  renderNetwork = (network: INetwork) => {
    const { connections } = this.props;
    const hasConnection = connections.some(conn => conn.provider === network.provider);
    const isActive = hasConnection || !network.auth;

    return isActive ? this.renderActiveNetwork(network) : this.renderInactiveNetwork(network);
  };

  render() {
    return NETWORKS.map(this.renderNetwork);
  }
}

export default enhancers(Networks);
